import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { onValue, ref } from 'firebase/database';

import FeaturedCard from './components/FeaturedCard';
import Category from './components/Category';
import ModeBar from './components/ModeBar';
import PhotoGrid from './components/PhotoGrid';
import PhotoFilters from './components/PhotoFilters';
import NotFound from './components/NotFound';
import useIsAdmin from './useIsAdmin';
import { saveCollection } from './catalogService';
import { savePhotos } from './photoService';

const FilmModal = lazy(() => import('./components/Modal'));
const PhotoModal = lazy(() => import('./components/PhotoModal'));
const CatalogEditor = lazy(() => import('./components/CatalogEditor'));
const PhotoEditor = lazy(() => import('./components/PhotoEditor'));

function ComingSoon({ icon, label }) {
    return (
        <div className="bb-soon">
            <div className="bb-soon__icon" aria-hidden="true">{icon}</div>
            <h2 className="bb-soon__title bb-display">{label} — bientôt</h2>
            <p>Cette section arrive prochainement.</p>
        </div>
    );
}

function EditorFallback() {
    return <p className="bb-category__empty">Chargement de l'éditeur…</p>;
}

const EDITABLE_MODES = ['films', 'livres', 'photos'];
const VALID_MODES = ['films', 'livres', 'photos', 'jeux'];

function findFilmByCode(collection, code) {
    for (const cat of collection) {
        const found = (cat.films || []).find((f) => f.code === code);
        if (found) return found;
    }
    return null;
}

const Home = () => {
    const [categories, setCategories] = useState([]);
    const [books, setBooks] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [filteredPhotos, setFilteredPhotos] = useState([]);
    const [featured, setFeatured] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const isAdmin = useIsAdmin();
    const { mode, id } = useParams();
    const navigate = useNavigate();

    useEffect(() => { setEditMode(false); }, [mode]);

    useEffect(() => {
        const onErr = (e) => console.error('Erreur de chargement :', e);
        const subs = [
            onValue(ref(db, 'categories'), (s) => setCategories(s.val() || []), onErr),
            onValue(ref(db, 'books'), (s) => setBooks(s.val() || []), onErr),
            onValue(ref(db, 'photos'), (s) => setPhotos(s.val() || []), onErr),
        ];
        return () => subs.forEach((unsub) => unsub());
    }, []);

    useEffect(() => {
        if (featured || !categories.length) return;
        const allFilms = categories.flatMap((c) => c.films || []).filter((f) => f && f.posterURL);
        if (allFilms.length) {
            setFeatured(allFilms[Math.floor(Math.random() * allFilms.length)]);
        }
    }, [categories, featured]);

    const editable = isAdmin && EDITABLE_MODES.includes(mode);
    const isEditing = editMode && editable;
    const collection = mode === 'livres' ? books : categories;

    // Ouverture/fermeture pilotées par l'URL → liens partageables.
    const openItem = (film) => navigate(`/${mode}/${encodeURIComponent(film.code)}`);
    const closeItem = () => navigate(`/${mode}`);

    const persistCollection = (newCollection) => {
        saveCollection(mode, newCollection).catch((e) => {
            console.error(e);
            alert("Échec de l'enregistrement. Vérifie tes droits (règles Firebase) et ta connexion.");
        });
    };
    const persistPhotos = (list) => {
        savePhotos(list).catch((e) => {
            console.error(e);
            alert("Échec de l'enregistrement des photos. Vérifie tes droits et ta connexion.");
        });
    };

    const renderRows = (coll, bookMode) => coll.map((category, index) => (
        <Category
            key={`${bookMode ? 'book-' : ''}${category.name || index}`}
            title={bookMode && category.name === 'Book' ? 'Livres' : category.name}
            films={category.films}
            onCardClick={openItem}
        />
    ));

    if (!VALID_MODES.includes(mode)) return <NotFound />;

    // Élément ouvert d'après l'URL
    const modalFilm = !isEditing && id && (mode === 'films' || mode === 'livres')
        ? findFilmByCode(collection, id)
        : null;
    // La lightbox/URL naviguent dans la liste filtrée ; repli sur la liste
    // complète si la photo ouverte (lien partagé) n'est pas dans le filtre.
    const photoList = filteredPhotos.some((p) => p.id === id) ? filteredPhotos : photos;
    const photoIndex = mode === 'photos' && id ? photoList.findIndex((p) => p.id === id) : -1;

    return (
        <div className="bb-home">
            <ModeBar mode={mode} onChange={(m) => navigate(`/${m}`)} />

            {editable && (
                <div className="bb-adminbar">
                    <button type="button" className={`bb-btn ${editMode ? 'bb-btn--primary' : ''}`} onClick={() => setEditMode((v) => !v)}>
                        {editMode ? "✓ Terminer l'édition" : '✏️ Modifier'}
                    </button>
                    {editMode && mode !== 'photos' && (
                        <span className="bb-adminbar__note">
                            Glisse les cartes pour réordonner (même d'une catégorie à l'autre). Clique une carte pour la modifier.
                        </span>
                    )}
                </div>
            )}

            {mode === 'films' && (
                isEditing
                    ? <Suspense fallback={<EditorFallback />}><CatalogEditor collection={categories} type="films" onPersist={persistCollection} /></Suspense>
                    : (
                        <>
                            {featured && (
                                <FeaturedCard film={featured} image={featured.posterURL} imagexs={featured.posterURL} onCardClick={openItem} />
                            )}
                            {renderRows(categories, false)}
                        </>
                    )
            )}

            {mode === 'livres' && (
                isEditing
                    ? <Suspense fallback={<EditorFallback />}><CatalogEditor collection={books} type="livres" onPersist={persistCollection} /></Suspense>
                    : (books.length ? renderRows(books, true) : <p className="bb-category__empty">Aucun livre pour l'instant.</p>)
            )}

            {mode === 'photos' && (
                isEditing
                    ? <Suspense fallback={<EditorFallback />}><PhotoEditor photos={photos} onPersist={persistPhotos} /></Suspense>
                    : (
                        <>
                            <PhotoFilters photos={photos} onFilter={setFilteredPhotos} />
                            <PhotoGrid photos={filteredPhotos} onSelect={(photo) => navigate(`/photos/${photo.id}`)} />
                        </>
                    )
            )}

            {mode === 'jeux' && <ComingSoon icon="🎮" label="Jeux" />}

            {modalFilm && (
                <Suspense fallback={null}>
                    <FilmModal film={modalFilm} onClose={closeItem} />
                </Suspense>
            )}
            {photoIndex >= 0 && (
                <Suspense fallback={null}>
                    <PhotoModal
                        photos={photoList}
                        index={photoIndex}
                        onNavigate={(i) => navigate(`/photos/${photoList[i].id}`, { replace: true })}
                        onClose={() => navigate('/photos')}
                    />
                </Suspense>
            )}
        </div>
    );
};

export default Home;
